import { forwardRef, useState } from 'react';
import {
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useAvailabilitySlotTypes } from '@product-framework/index';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  COPY_ALL_OTHER_DAYS,
  COPY_NONE,
  CUTOFF_KEY,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT_LABELS,
} from '../../../utils/constants';
import IOSSwitch from './IOSSwitch';
import CutoffTimeDialog from './CutoffTimeDialog';
import lib, { getDefaultCutoffForWeekday } from '../../../utils/lib';

const SCHEDULE_GRID_SELECT_SX = {
  minWidth: 128,
  maxWidth: 1,
  width: '100%',
  typography: 'caption',
};

const ScheduleComponent = forwardRef(
  (
    {
      showEditingControls,
      setDraft,
      setSlot,
      handleCancel,
      handleSave,
      handleReset,
      openCutoffDialog,
      params,
      onChange,
      ...props
    },
    ref,
  ) => {
    const { control } = useFormContext();
    const value = useWatch({ control, name: props?.field?.name });

    const [selectedCutoffWeekday, setSelectedCutoffWeekday] = useState(null);

    const { slotTypes } = useAvailabilitySlotTypes();

    const getUpdatedValue = ({
      action = '',
      slotInfo,
      cutoffInfo,
      copyInfo,
    }) => {
      let newValue = value;

      switch (action) {
        case 'UPDATE_SLOT': {
          newValue = {
            ...value,
            [slotInfo?.weekday]: {
              ...value[slotInfo?.weekday],
              [slotInfo?.key]: slotInfo?.value,
            },
          };
          // If not slot was selected, and new slot is checked, adjust the cuttoff time
          if (!value?.[slotInfo.weekday]?.cutoff?.time) {
            const defaultCutoff = getDefaultCutoffForWeekday(slotInfo.weekday);
            newValue = {
              ...value,
              [slotInfo.weekday]: {
                ...newValue[slotInfo.weekday],
                cutoff: defaultCutoff,
              },
            };
          }
          // If not slots were selected remove cutoff
          if (
            !slotTypes.some(
              st => newValue[slotInfo.weekday]?.[st.code] === true,
            )
          ) {
            newValue = {
              ...value,
              [slotInfo.weekday]: {
                ...newValue[slotInfo.weekday],
                cutoff: {},
              },
            };
          }
          break;
        }
        case 'UPDATE_CUTOFF_TIME': {
          const { weekday, ...other } = cutoffInfo;
          newValue = {
            ...value,
            [weekday]: {
              ...value[weekday],
              cutoff: other,
            },
          };
          break;
        }
        case 'COPY_TO_WEEK': {
          newValue = {
            ...value,
            [copyInfo?.destinationWeekday]: {
              ...value[copyInfo?.sourceWeekday],
            },
          };
          break;
        }
        case 'COPY_ALL_OTHER_DAYS': {
          newValue = WEEKDAY_KEYS.reduce((acc, item) => {
            return {
              ...acc,
              [item]: value[copyInfo?.sourceWeekday],
            };
          }, {});
          break;
        }
        default:
      }
      return {
        target: { name: props?.field?.name, value: { ...newValue } },
      };
    };

    const applyCopyFromWeekday = (fromWk, toWk) => {
      if (toWk === COPY_ALL_OTHER_DAYS) {
        const newValue = getUpdatedValue({
          action: 'COPY_ALL_OTHER_DAYS',
          copyInfo: {
            sourceWeekday: fromWk,
          },
        });

        onChange(newValue);
      } else {
        const newValue = getUpdatedValue({
          action: 'COPY_TO_WEEK',
          copyInfo: {
            sourceWeekday: fromWk,
            destinationWeekday: toWk,
          },
        });
        onChange(newValue);
      }
    };

    return (
      <Controller
        name="selectedDateOption"
        control={control}
        render={() => {
          return (
            <Paper ref={ref} variant="outlined" sx={{ p: 2 }}>
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    borderCollapse: 'collapse',
                    '& .MuiTableCell-root': {
                      border: 'none',
                      py: 1.25,
                      px: 1,
                    },
                  }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {WEEKDAY_KEYS.map(wk => (
                        <TableCell key={wk} align="center">
                          <Stack
                            spacing={1}
                            alignItems="center"
                            sx={{ width: '100%' }}>
                            <Typography variant="subtitle2">
                              {WEEKDAY_SHORT_LABELS[wk]}
                            </Typography>
                            <Select
                              sx={{
                                ...SCHEDULE_GRID_SELECT_SX,
                                width: '60%',
                                minWidth: '100px',
                                '& .MuiSelect-select': {
                                  py: 0.5, // reduce vertical padding
                                  lineHeight: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                },
                              }}
                              size="small"
                              variant="outlined"
                              displayEmpty
                              value={COPY_NONE}
                              onChange={e => {
                                const toWk = e.target.value;
                                if (
                                  toWk &&
                                  toWk !== COPY_NONE &&
                                  (toWk === COPY_ALL_OTHER_DAYS ||
                                    WEEKDAY_KEYS.includes(toWk))
                                ) {
                                  applyCopyFromWeekday(wk, toWk);
                                }
                              }}
                              renderValue={selected => {
                                if (!selected || selected === COPY_NONE) {
                                  return (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary">
                                      <FormattedMessage
                                        id="COPY_TO"
                                        defaultMessage="Copy To"
                                      />
                                    </Typography>
                                  );
                                }
                                if (selected === COPY_ALL_OTHER_DAYS) {
                                  return (
                                    <Typography variant="caption">
                                      <FormattedMessage
                                        id="ALL_OTHER_DAYS"
                                        defaultMessage="All Other Days"
                                      />
                                    </Typography>
                                  );
                                }
                                return WEEKDAY_LABELS[selected];
                              }}>
                              <MenuItem
                                value={COPY_NONE}
                                sx={{ display: 'none' }}>
                                {' '}
                              </MenuItem>
                              <MenuItem value={COPY_ALL_OTHER_DAYS}>
                                <FormattedMessage
                                  id="ALL_OTHER_DAYS"
                                  defaultMessage="All Other Days"
                                />
                              </MenuItem>
                              {WEEKDAY_KEYS.filter(x => x !== wk).map(
                                target => (
                                  <MenuItem key={target} value={target}>
                                    {WEEKDAY_LABELS[target]}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </Stack>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slotTypes.map(sk => (
                      <TableRow key={sk?.code}>
                        <TableCell align="right">
                          <Typography variant="subtitle2">{sk.name}</Typography>
                        </TableCell>
                        {WEEKDAY_KEYS.map(wk => {
                          const slotOn = Boolean(value?.[wk]?.[sk?.code]);
                          const slotContent = (
                            <IOSSwitch
                              checked={slotOn}
                              onChange={e => {
                                const newValue = getUpdatedValue({
                                  action: 'UPDATE_SLOT',
                                  slotInfo: {
                                    weekday: wk,
                                    key: sk.code,
                                    value: e.target.checked,
                                  },
                                });
                                onChange(newValue);
                              }}
                            />
                          );
                          return (
                            <TableCell key={`${wk}-${sk?.code}`} align="center">
                              {slotContent}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell align="right">
                        <Typography variant="subtitle2">
                          <FormattedMessage
                            id="CUTOFF_TIME"
                            defaultMessage="Cutoff time"
                          />
                        </Typography>
                      </TableCell>
                      {WEEKDAY_KEYS.map(wk => (
                        <TableCell key={`${wk}-cutoff`} align="center">
                          <Button
                            disabled={!value[wk]?.[CUTOFF_KEY]?.time}
                            variant="outlined"
                            size="small"
                            onClick={() => setSelectedCutoffWeekday(wk)}
                            sx={{
                              maxWidth: '100%',
                              typography: 'caption',
                              whiteSpace: 'normal',
                              textAlign: 'center',
                            }}>
                            {!value[wk]?.[CUTOFF_KEY]?.time ? (
                              <FormattedMessage
                                id="PLEASE_SELECT_SLOT"
                                defaultMessage="Please Select Slot"
                              />
                            ) : (
                              lib.formatCutoffSummary(
                                wk,
                                value[wk]?.[CUTOFF_KEY],
                              )
                            )}
                          </Button>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedCutoffWeekday && (
                <CutoffTimeDialog
                  selectedCutoffWeekday={selectedCutoffWeekday}
                  setSelectedCutoffWeekday={setSelectedCutoffWeekday}
                  control={control}
                  name={props?.field?.name}
                  onChange={co => {
                    const newValue = getUpdatedValue({
                      action: 'UPDATE_CUTOFF_TIME',
                      cutoffInfo: co,
                    });
                    onChange(newValue);
                  }}
                />
              )}
            </Paper>
          );
        }}
      />
    );
  },
);

export default ScheduleComponent;
